import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type PosStore = {
  _id: string
  name: string
  slug?: string
  address?: string
  type?: string
  is_default?: boolean
}

type PosStoreState = {
  store: PosStore | null
  availableStores: PosStore[]
  storesLoaded: boolean
  setStore: (store: PosStore | null) => void
  setAvailableStores: (stores: PosStore[]) => void
  clearStore: () => void
  /** Pick previous if still assigned, else defaultId, else first store */
  ensureActiveStore: (stores: PosStore[], defaultStoreId?: string | null) => PosStore | null
}

export function pickDefaultPosStore(
  stores: PosStore[],
  currentId?: string | null,
  defaultStoreId?: string | null,
): PosStore | null {
  if (!stores.length) return null
  if (currentId) {
    const previous = stores.find((s) => s._id === currentId)
    if (previous) return previous
  }
  if (defaultStoreId) {
    const preferred = stores.find((s) => s._id === defaultStoreId)
    if (preferred) return preferred
  }
  const flagged = stores.find((s) => s.is_default)
  return flagged || stores[0]
}

export const usePosStoreContext = create<PosStoreState>()(
  persist(
    (set, get) => ({
      store: null,
      availableStores: [],
      storesLoaded: false,
      setStore: (store) => set({ store }),
      setAvailableStores: (stores) => set({ availableStores: stores, storesLoaded: true }),
      clearStore: () => set({ store: null, availableStores: [], storesLoaded: false }),
      ensureActiveStore: (stores, defaultStoreId) => {
        const next = pickDefaultPosStore(stores, get().store?._id, defaultStoreId)
        set({
          availableStores: stores,
          storesLoaded: true,
          store: next,
        })
        return next
      },
    }),
    {
      name: 'pos-active-store',
      partialize: (state) => ({ store: state.store }),
    },
  ),
)
